import { ApiError, type ApiResponse } from "@template/types";

/**
 * Layering note: this package (api) knows nothing about Redux or any
 * particular state management. When an access token expires (401), it needs
 * *someone* to perform a refresh and hand back a new access token. That
 * "someone" is registered from the `state` package via
 * `configureAuthRecovery()` at app startup (see
 * packages/state/src/auth/auth.thunks.ts). This keeps the dependency arrow
 * pointing one way: state -> api, never api -> state.
 */
export type AuthRecoveryHandler = () => Promise<string | null>;

export interface ApiClientConfig {
  baseUrl: string;
}

type HttpMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

interface RequestOptions {
  body?: unknown;
  signal?: AbortSignal;
  /** Skip attaching the Authorization header (e.g. for login/refresh calls). */
  skipAuth?: boolean;
  /** Internal: prevents infinite refresh loops. */
  _isRetry?: boolean;
}

let baseUrl = "";
let accessToken: string | null = null;
let authRecoveryHandler: AuthRecoveryHandler | null = null;

/** Single-flight promise so concurrent 401s trigger only one refresh call. */
let pendingRefresh: Promise<string | null> | null = null;

export function initializeApiClient(config: ApiClientConfig): void {
  baseUrl = config.baseUrl.replace(/\/+$/, "");
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function clearAccessToken(): void {
  accessToken = null;
}

/**
 * Registered once at app startup by the `state` package. Called whenever a
 * request comes back 401, to obtain a fresh access token. Should return the
 * new access token on success, or `null` if the refresh itself failed (at
 * which point the caller gives up and the app should force a logout).
 */
export function configureAuthRecovery(handler: AuthRecoveryHandler | null): void {
  authRecoveryHandler = handler;
}

function buildUrl(path: string): string {
  if (/^https?:\/\//.test(path)) return path;
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
}

async function parseEnvelope<T>(response: Response): Promise<ApiResponse<T>> {
  const text = await response.text();
  if (!text) {
    // No body (e.g. 204 No Content) — synthesize a minimal envelope.
    return {
      success: response.ok,
      statusCode: response.status,
      data: null as T | null,
      error: response.ok
        ? null
        : { code: "EMPTY_RESPONSE", message: response.statusText || "Request failed" },
      timestamp: new Date().toISOString(),
      path: response.url,
    };
  }
  try {
    return JSON.parse(text) as ApiResponse<T>;
  } catch {
    throw new ApiError(
      { code: "INVALID_JSON", message: "Server returned a non-JSON response" },
      response.status,
    );
  }
}

async function rawRequest<T>(
  method: HttpMethod,
  path: string,
  options: RequestOptions = {},
): Promise<{ response: Response; envelope: ApiResponse<T> }> {
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (!options.skipAuth && accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const response = await fetch(buildUrl(path), {
    method,
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  });

  const envelope = await parseEnvelope<T>(response);
  return { response, envelope };
}

async function refreshAccessToken(): Promise<string | null> {
  if (!authRecoveryHandler) return null;
  if (!pendingRefresh) {
    pendingRefresh = authRecoveryHandler().finally(() => {
      pendingRefresh = null;
    });
  }
  return pendingRefresh;
}

async function request<T>(
  method: HttpMethod,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { response, envelope } = await rawRequest<T>(method, path, options);

  if (response.status === 401 && !options.skipAuth && !options._isRetry) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      return request<T>(method, path, { ...options, _isRetry: true });
    }
    // Refresh failed (or no handler registered) — surface the original 401.
  }

  if (!envelope.success) {
    const error = envelope.error ?? {
      code: "UNKNOWN_ERROR",
      message: response.statusText || "Request failed",
    };
    throw new ApiError(error, envelope.statusCode ?? response.status);
  }

  return envelope.data as T;
}

export const apiClient = {
  get: <T>(path: string, options?: RequestOptions) => request<T>("GET", path, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("POST", path, { ...options, body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("PATCH", path, { ...options, body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("PUT", path, { ...options, body }),
  delete: <T>(path: string, options?: RequestOptions) => request<T>("DELETE", path, options),
};
